import React from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';

function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    const completedVal = payload.find((p) => p.dataKey === 'completed')?.value || 0;
    const targetVal = payload.find((p) => p.dataKey === 'target')?.value || 0;
    return (
      <div className="bg-[#131318] border border-[#2C2C34] rounded-xl p-3 shadow-xl text-xs space-y-1">
        <div className="font-semibold text-[#F2F2F4] border-b border-[#2C2C34] pb-1 mb-1">{label}</div>
        <div className="flex items-center justify-between gap-4 text-emerald-400">
          <span>Completed Tasks:</span>
          <span className="font-bold">{completedVal}</span>
        </div>
        <div className="flex items-center justify-between gap-4 text-[#D42B92]">
          <span>Assigned Target:</span>
          <span className="font-bold">{targetVal}</span>
        </div>
      </div>
    );
  }
  return null;
}

export default function KraKpiChart({
  buckets = [],
  headlinePercentage,
  numerator = 0,
  denominator = 0,
  period = 'this_month',
  type = 'all',
  onPeriodChange,
  onTypeChange,
}) {
  const hasData = buckets.length > 0 && denominator > 0;

  return (
    <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-5">
      {/* Top Header & Filters matching PDF Page 3 Box 2 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-[#F2F2F4] tracking-tight">
              {headlinePercentage !== null ? `${headlinePercentage}%` : 'N/A'}
            </span>
            <span className="text-xs text-[#8E8E99]">
              {hasData
                ? `completion rate (${numerator} of ${denominator} tasks done)`
                : 'No assigned KRA/KPI tasks for this period'}
            </span>
          </div>
          <div className="text-xs font-medium text-[#8E8E99] mt-0.5">
            KRA & KPI Performance Completion
          </div>
        </div>

        {/* Filter Controls */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Period Filter (This month, Quarter, Year) */}
          <div className="flex items-center bg-[#08080A] p-0.5 rounded-xl border border-[#2C2C34]">
            {[
              { id: 'this_month', label: 'This month' },
              { id: 'quarter', label: 'Quarter' },
              { id: 'year', label: 'Year' },
            ].map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onPeriodChange(p.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  period === p.id
                    ? 'bg-[#321129] text-[#F2F2F4] border border-[#D42B92]/40 shadow-sm'
                    : 'text-[#8E8E99] hover:text-[#F2F2F4]'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Type Toggle (All / KRA / KPI) */}
          <div className="flex items-center bg-[#08080A] p-0.5 rounded-xl border border-[#2C2C34]">
            {[
              { id: 'all', label: 'All' },
              { id: 'kra', label: 'KRA' },
              { id: 'kpi', label: 'KPI' },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => onTypeChange(t.id)}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  type === t.id
                    ? 'bg-[#D42B92] text-white shadow-sm'
                    : 'text-[#8E8E99] hover:text-[#F2F2F4]'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Recharts Canvas */}
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={buckets} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2C2C34" vertical={false} opacity={0.6} />
            <XAxis
              dataKey="label"
              stroke="#8E8E99"
              fontSize={11}
              tickLine={false}
              axisLine={{ stroke: '#2C2C34' }}
            />
            <YAxis
              stroke="#8E8E99"
              fontSize={11}
              tickLine={false}
              axisLine={{ stroke: '#2C2C34' }}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="completed"
              fill="#D42B92"
              radius={[6, 6, 0, 0]}
              maxBarSize={40}
              name="Tasks Completed"
            />
            <Line
              type="monotone"
              dataKey="target"
              stroke="#38BDF8"
              strokeWidth={2}
              dot={{ r: 4, fill: '#38BDF8' }}
              name="Assigned / Target"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Legend & Note */}
      <div className="flex flex-wrap items-center justify-between text-xs text-[#8E8E99] pt-2 border-t border-[#2C2C34]/50">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-[#D42B92] inline-block" />
            <span>Tasks Completed (persisted Done)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-4 h-0.5 bg-[#38BDF8] inline-block" />
            <span>Assigned Target Line</span>
          </div>
        </div>
        <div>Hover over bars for exact count per period.</div>
      </div>
    </div>
  );
}
